import { useState, useEffect } from 'react';
import ContactSearch from './ContactSearch';
import ProductSelector from './ProductSelector';

export default function QuotationForm({ data, catalog, onChange }) {
  const { products = [], kits = [], campaigns = [], agents = [] } = catalog || {};
  const [campaignPrices, setCampaignPrices] = useState([]);
  const [showAlternatives, setShowAlternatives] = useState(false);

  // Load campaign prices when campaign changes
  useEffect(() => {
    if (data.campaign_id) {
      import('../../services/quotationService').then(({ quotationCatalogAPI }) => {
        quotationCatalogAPI.getCampaignPrices(data.campaign_id).then(setCampaignPrices).catch(console.error);
      });
    } else {
      setCampaignPrices([]);
    }
  }, [data.campaign_id]);

  // Recalculate totals when items change
  useEffect(() => {
    const items = data.items || [];
    const alts = data.alternatives || [];
    const allItems = [...items, ...alts];

    const getPrice = (item) => {
      if (item.override_price != null) return item.override_price;
      if (item.discount_price != null) return item.discount_price;
      return item.normal_price;
    };

    const totalNeto = allItems.reduce((sum, item) => sum + (getPrice(item) * (item.quantity || 1)), 0);
    const ivaAmount = Math.round(totalNeto * 0.19);
    const totalConIva = totalNeto + ivaAmount;

    if (totalNeto !== data.total_neto || ivaAmount !== data.iva_amount || totalConIva !== data.total_con_iva) {
      onChange({ ...data, total_neto: totalNeto, iva_amount: ivaAmount, total_con_iva: totalConIva });
    }
  }, [data.items, data.alternatives]);

  const update = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleContactSelect = ({ phone, name }) => {
    onChange({ ...data, prospect_phone: phone, client_name: name });
  };

  const handleCampaignChange = (campaignId) => {
    const campaign = campaigns.find(c => c.id === parseInt(campaignId));
    const updates = { campaign_id: campaignId ? parseInt(campaignId) : null };
    if (campaign) {
      updates.validity_start = campaign.start_date;
      updates.validity_end = campaign.end_date;
    }
    onChange({ ...data, ...updates });
  };

  const formatPrice = (n) => {
    if (n == null) return '$0';
    return '$' + n.toLocaleString('es-CL');
  };

  return (
    <div className="space-y-5">
      {/* Contact search */}
      <ContactSearch onSelect={handleContactSelect} />

      {/* Client info */}
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del cliente</label>
          <input
            type="text"
            value={data.client_name || ''}
            onChange={(e) => update('client_name', e.target.value)}
            placeholder="Nombre completo"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tratamiento</label>
          <select
            value={data.client_greeting || 'Estimado/a'}
            onChange={(e) => update('client_greeting', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="Estimado/a">Estimado/a</option>
            <option value="Estimado">Estimado</option>
            <option value="Estimada">Estimada</option>
          </select>
        </div>
      </div>

      {/* Agent + Campaign */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Asesor</label>
          <select
            value={data.agent_identity_id || ''}
            onChange={(e) => update('agent_identity_id', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Seleccionar asesor...</option>
            {agents.map(a => (
              <option key={a.agent_identity_id} value={a.agent_identity_id}>{a.name} - {a.role_title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Campana</label>
          <select
            value={data.campaign_id || ''}
            onChange={(e) => handleCampaignChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Sin campana</option>
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Product selector */}
      <ProductSelector
        products={products}
        kits={kits}
        campaignPrices={campaignPrices}
        selectedItems={data.items || []}
        onItemsChange={(items) => update('items', items)}
      />

      {/* Alternatives toggle */}
      <div className="border-t border-gray-200 pt-4">
        <button
          onClick={() => setShowAlternatives(!showAlternatives)}
          className="text-sm text-blue-600 hover:underline"
        >
          {showAlternatives ? '- Ocultar alternativas' : '+ Otras alternativas'}
        </button>
        {showAlternatives && (
          <div className="mt-3">
            <ProductSelector
              products={products}
              kits={[]}
              campaignPrices={campaignPrices}
              selectedItems={data.alternatives || []}
              onItemsChange={(items) => update('alternatives', items)}
              isAlternatives={true}
            />
          </div>
        )}
      </div>

      {/* Validity dates */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Validez desde</label>
          <input
            type="date"
            value={data.validity_start || ''}
            onChange={(e) => update('validity_start', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Validez hasta</label>
          <input
            type="date"
            value={data.validity_end || ''}
            onChange={(e) => update('validity_end', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
        <textarea
          value={data.notes || ''}
          onChange={(e) => update('notes', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
          placeholder="Notas adicionales..."
        />
      </div>

      {/* Totals */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal Neto:</span>
          <span className="font-medium">{formatPrice(data.total_neto)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">IVA (19%):</span>
          <span className="font-medium">{formatPrice(data.iva_amount)}</span>
        </div>
        <div className="flex justify-between text-base font-bold border-t border-gray-300 pt-2">
          <span>Total con IVA:</span>
          <span className="text-blue-700">{formatPrice(data.total_con_iva)}</span>
        </div>
      </div>
    </div>
  );
}
